import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import SEO, { breadcrumb, faqSchema, softwareApplicationSchema } from "@/components/SEO";
import {
  ArrowRight, Sparkles, Wand2, ShieldCheck, Network, Lock,
  Users, Stethoscope, Building2, CheckCircle2, Clock, Zap,
  Video, MailCheck, CalendarDays, FileText, Activity,
} from "lucide-react";

/* ----------------------------------------------------------------------
 * PERSONA CONFIGURATIONS
 * Each entry drives an entire SEO-optimized landing page.
 * Keep texts dense in target keywords and natural for humans.
 * -------------------------------------------------------------------- */
const PERSONAS = {
  cpts: {
    slug: "cpts",
    title: "Plateforme CPTS clé en main · MetaConnect Studio",
    description: "Plateforme sur-mesure pour CPTS : coordination entre praticiens, IDE et pharmaciens, messagerie MSSanté partagée, prise de rendez-vous territoriale.",
    eyebrow: "CPTS · Communauté Professionnelle Territoriale de Santé",
    h1Pre: "Donnez à votre",
    h1Highlight: "CPTS",
    h1Post: "une plateforme territoriale en 5 minutes.",
    intro: "MetaConnect Studio configure pour votre <strong>Communauté Professionnelle Territoriale de Santé</strong> une plateforme unique qui orchestre médecins généralistes, infirmiers libéraux, sages-femmes, pharmaciens et masseurs-kinésithérapeutes autour d'un parcours patient coordonné — sans devis commercial, sans cycle de vente long.",
    pains: [
      { i: Users, t: "Coordination dispersée", d: "Vos PS échangent par téléphone, SMS, mails non sécurisés. Aucune trace, aucune continuité du parcours patient." },
      { i: Clock, t: "Délais d'accès aux soins", d: "Les patients du territoire attendent 4 à 8 semaines pour un RDV. Vous n'avez pas d'outil pour mutualiser les disponibilités." },
      { i: FileText, t: "Reporting ARS chronophage", d: "Chaque trimestre, compilation manuelle des actes coordonnés pour le contrat ACI. Vous y passez 10-15 h." },
    ],
    bricks: [
      { i: CalendarDays, t: "Agenda territorial mutualisé", d: "Tous vos PS partagent un agenda commun. Les patients voient les premiers créneaux disponibles, peu importe le praticien — réduction de 60 % du délai d'accès." },
      { i: MailCheck, t: "Messagerie MSSanté CPTS", d: "Une adresse <code>contact@votre-cpts.mssante.fr</code> + adresses individuelles par PS. Échanges patients, hospitaliers et confrères tracés et sécurisés." },
      { i: Video, t: "Téléconsultation coordonnée", d: "Un patient en visio avec son MG peut être réorienté en direct vers l'IDE ou la sage-femme. Salle d'attente partagée, transmission au DMP." },
      { i: Activity, t: "Reporting ACI automatisé", d: "Les actes coordonnés sont comptabilisés automatiquement par typologie. Export CSV/PDF en 1 clic pour l'ARS. Gain : 10 h/trimestre." },
    ],
    keyMetrics: [
      { v: "60 %", l: "Délai d'accès réduit" },
      { v: "10 h", l: "Reporting ACI gagné /trimestre" },
      { v: "100 %", l: "Traçabilité des échanges PS" },
    ],
    faq: [
      { q: "Notre CPTS doit-elle changer son agenda existant ?", a: "Non. MetaConnect Studio peut soit remplacer votre agenda actuel (Doctolib, Maiia), soit s'intégrer en complément. Lors du cadrage, nous étudions votre stack existant pour préserver la continuité." },
      { q: "Comment sont gérés les rôles dans une CPTS multi-PS ?", a: "Système de rôles Spatie Teams : Coordinateur, Médecin référent, IDEL, Sage-femme, Kiné, Pharmacien, Secrétariat. Chacun voit uniquement les patients et messages auxquels il a accès — conformité RGPD by design." },
      { q: "Est-ce éligible au financement ACI (Avenant 9) ?", a: "Oui. Les volets coordination, parcours et accès aux soins du contrat ACI sont directement adressés par les fonctionnalités MetaConnect. Nous fournissons les exports demandés par l'ARS." },
      { q: "Combien de praticiens peuvent rejoindre la plateforme ?", a: "Aucune limite. La tarification est dégressive : ~9 €/mois/PS jusqu'à 10 PS, puis dégressive jusqu'à 4 €/mois/PS au-delà de 50. Devis précis post-cadrage." },
    ],
    quote: { who: "Dr Camille R., MG · CPTS Loire-Sud", text: "On a configuré notre plateforme en 7 minutes. Le lendemain, je l'ai présentée en bureau CPTS via le lien d'aperçu — décision votée à l'unanimité." },
  },
  "cabinet-liberal": {
    slug: "cabinet-liberal",
    title: "Téléconsultation pour cabinet libéral · MetaConnect Studio",
    description: "Pour un cabinet libéral solo ou à deux : site internet professionnel, prise de rendez-vous en ligne, messagerie MSSanté, visio conforme HAS. Sans engagement.",
    eyebrow: "Cabinet libéral · Médecin solo ou petit cabinet",
    h1Pre: "Votre cabinet libéral mérite",
    h1Highlight: "son propre site",
    h1Post: "et sa téléconsultation.",
    intro: "MetaConnect Studio génère pour <strong>votre cabinet libéral</strong> un site internet professionnel référencé Google, une prise de rendez-vous en ligne et une téléconsultation HAS conforme — le tout sans payer un développeur ni dépendre de Doctolib. <strong>Vous gardez 100 % de vos données patients et de votre image.</strong>",
    pains: [
      { i: Building2, t: "Pas de site internet", d: "Vous êtes invisible sur Google. Vos confrères concurrents capturent les nouveaux patients du quartier." },
      { i: CalendarDays, t: "RDV par téléphone", d: "Votre secrétaire passe 4 h/jour au téléphone. Patients perdus le soir et le weekend." },
      { i: Video, t: "Téléconsultation absente", d: "Vous perdez les patients chroniques qui demandent du suivi à distance, ils partent chez la concurrence numérisée." },
    ],
    bricks: [
      { i: Building2, t: "Fiche vitrine SEO optimisée", d: "Site internet à vos couleurs, indexé Google : nom, adresse, spécialités, horaires, avis patients, photo du cabinet. Référencement local automatique sur votre commune." },
      { i: CalendarDays, t: "Prise de RDV en ligne 24/7", d: "Vos patients réservent sans appeler. Synchronisation avec votre agenda professionnel. Rappels SMS et email automatiques — réduction du no-show de 40 %." },
      { i: Video, t: "Téléconsultation HAS conforme", d: "Visio chiffrée bout-en-bout, salle d'attente virtuelle, transmission ordonnance au patient et dépôt automatique au DMP. Cotation TCG/TCD disponible." },
      { i: MailCheck, t: "Messagerie MSSanté pro", d: "Adresse <code>dr.votrenom@medecin.mssante.fr</code> pour échanger avec confrères, hôpitaux et laboratoires en toute sécurité." },
    ],
    keyMetrics: [
      { v: "40 %", l: "No-show en moins" },
      { v: "4 h/j", l: "Temps secrétariat gagné" },
      { v: "x3", l: "Visibilité Google locale" },
    ],
    faq: [
      { q: "Je n'ai pas de compétences techniques. C'est compliqué ?", a: "Non. Le wizard MetaConnect Studio se fait au clic, sans aucun code ni configuration technique. Mise en ligne par notre équipe sous 48 h ouvrées après validation." },
      { q: "Vais-je remplacer Doctolib ?", a: "Si vous le souhaitez, oui. La différence : avec MetaConnect, vous payez un abonnement fixe (à partir de 49 €/mois) au lieu d'un % sur chaque téléconsultation, et vous gardez votre liste patients privée — Doctolib les commercialise pour son propre annuaire." },
      { q: "Mes données patients sont-elles sécurisées ?", a: "Hébergement HDS chez un hébergeur de Données de Santé certifié français (Scaleway / OVH). RGPD by design. Vous restez seul propriétaire des données — nous ne les exploitons jamais." },
      { q: "Quel est le tarif minimum pour un médecin seul ?", a: "À partir de 49 €/mois pour un médecin solo (site vitrine + RDV en ligne + téléconsultation illimitée + messagerie MSSanté). Pas d'engagement, résiliation à tout moment. PDF de devis post-aperçu Studio." },
    ],
    quote: { who: "Dr Léa T., généraliste · Orléans", text: "J'ai configuré ma plateforme un dimanche soir. 48 h après, mon site était en ligne et 6 patients avaient déjà pris RDV via la téléconsultation. Game changer." },
  },
  msp: {
    slug: "msp",
    title: "Plateforme MSP clé en main · MetaConnect Studio",
    description: "Pour une maison de santé pluriprofessionnelle : projet de santé partagé, dossier commun, agenda multi-praticiens, MSSanté. Conforme au cahier des charges ARS.",
    eyebrow: "MSP · Maison de Santé Pluriprofessionnelle",
    h1Pre: "Votre",
    h1Highlight: "Maison de Santé",
    h1Post: "mérite une plateforme à la hauteur.",
    intro: "MetaConnect Studio configure pour votre <strong>Maison de Santé Pluriprofessionnelle</strong> une infrastructure numérique qui réunit tous vos professionnels autour d'un dossier patient commun, d'un agenda partagé et d'un parcours coordonné — conforme au <strong>cahier des charges ARS</strong> et au projet de santé.",
    pains: [
      { i: Users, t: "Dossier patient cloisonné", d: "Chaque PS a son propre logiciel métier. Aucun vue commune sur les antécédents, allergies, traitements. Risque iatrogène." },
      { i: CalendarDays, t: "Plannings non synchronisés", d: "Les patients adressés au sein de la MSP ne peuvent pas réserver en ligne — il faut décrocher le téléphone." },
      { i: FileText, t: "Reporting projet de santé", d: "L'ARS demande des indicateurs précis (file active, parcours coordonnés). Vous les compilez à la main sous Excel." },
    ],
    bricks: [
      { i: Users, t: "Dossier patient pluriprofessionnel", d: "Vue 360° du patient accessible à tous les PS de la MSP (avec gestion fine des droits d'accès). Antécédents, traitements, examens biologiques, comptes-rendus partagés." },
      { i: CalendarDays, t: "Agenda MSP commun", d: "Tous vos professionnels exposent leurs disponibilités sur une interface unique. Le patient choisit le bon créneau avec le bon PS — diminution drastique du délai de prise en charge." },
      { i: Video, t: "Téléconsultation MSP", d: "Le MG en visio avec un patient peut faire entrer en direct l'IDE de la MSP pour un soin ou éducation thérapeutique. Workflow révolutionnaire." },
      { i: ShieldCheck, t: "Conformité cahier des charges ARS", d: "Pro Santé Connect, INSi qualifié, DMP, MSSanté, hébergement HDS. Tous les critères du cahier des charges ARS pour les MSP sont nativement remplis." },
    ],
    keyMetrics: [
      { v: "100 %", l: "Conforme cahier ARS" },
      { v: "x4", l: "Coordination accélérée" },
      { v: "0 €", l: "Aperçu et CDC" },
    ],
    faq: [
      { q: "Nous sommes 12 PS de spécialités différentes. C'est gérable ?", a: "Absolument. MetaConnect Studio gère jusqu'à plusieurs dizaines de PS avec des rôles différenciés (MG, IDE, sage-femme, kiné, ortho, psy, diét, pharmacien…). La plateforme s'adapte à votre projet de santé." },
      { q: "Pouvons-nous garder nos logiciels métier existants ?", a: "Oui. MetaConnect s'interface avec les logiciels métier existants via FHIR France et HL7 V2. Le dossier patient pluriprofessionnel n'écrase pas vos dossiers individuels, il les unifie en consultation." },
      { q: "Quelles sont les obligations RGPD pour une MSP ?", a: "Le partage de données entre PS de la MSP est encadré par l'article L.1110-12 du Code de la santé publique. MetaConnect implémente le consentement éclairé patient, l'opposabilité et l'audit trail complet. DPO disponible pour votre dossier CNIL." },
      { q: "Combien coûte une plateforme pour une MSP ?", a: "Tarification à partir de ~6 €/mois/PS pour une MSP de 8-15 PS. Devis précis post-cadrage. Éligible aux financements ARS dans le cadre du projet de santé." },
    ],
    quote: { who: "Aurélie M., coordinatrice · MSP du Val", text: "Le cahier des charges ARS est notre cauchemar depuis 2 ans. MetaConnect Studio nous a montré en 10 minutes une plateforme qui coche 100 % des cases. On a signé." },
  },
};

export default function StudioPersona() {
  const { slug } = useParams();
  const cfg = PERSONAS[slug];
  if (!cfg) return <Navigate to="/studio" replace />;

  const schema = [
    softwareApplicationSchema,
    breadcrumb([
      { name: "Accueil", url: "/" },
      { name: "Studio", url: "/studio" },
      { name: cfg.eyebrow, url: `/studio/${cfg.slug}` },
    ]),
    faqSchema(cfg.faq),
  ];

  return (
    <div className="bg-[var(--mc-navy-950)] min-h-screen" data-testid={`page-persona-${cfg.slug}`}>
      <SEO title={cfg.title} description={cfg.description} canonical={`/studio/${cfg.slug}`} schema={schema} />

      {/* HERO */}
      <section className="relative overflow-hidden hex-grid pt-28 pb-16 lg:pt-36 lg:pb-20">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[820px] h-[820px] rounded-full" style={{ background: "radial-gradient(circle, rgba(45,123,255,0.22), transparent 60%)" }} />
          <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full" style={{ background: "radial-gradient(circle, rgba(110,231,197,0.18), transparent 60%)" }} />
        </div>
        <div className="relative max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 glow-pill px-4 py-1.5 rounded-full text-[12px]">
            <Sparkles size={13} className="text-emerald-600" />
            <span className="text-emerald-600 font-semibold">{cfg.eyebrow}</span>
          </div>
          <h1 className="font-display font-semibold text-[#0F2B5B] text-[36px] sm:text-5xl lg:text-[68px] leading-[1.02] tracking-[-0.025em] mt-7">
            {cfg.h1Pre} <span className="bg-gradient-to-r from-[#7FB3FF] via-[#4F8FFF] to-[#2D7BFF] bg-clip-text text-transparent">{cfg.h1Highlight}</span>{" "}{cfg.h1Post}
          </h1>
          <p className="mt-6 mx-auto text-[15.5px] lg:text-[17px] leading-relaxed text-[#475569] max-w-3xl" dangerouslySetInnerHTML={{ __html: cfg.intro }} />
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/studio" className="btn-primary px-7 py-4 rounded-full text-[15.5px] font-semibold inline-flex items-center gap-2 shadow-[0_12px_40px_rgba(110,231,197,0.30)]" data-testid="persona-cta-primary">
              <Wand2 size={17} /> Configurer ma plateforme · 5 min <ArrowRight size={16} />
            </Link>
            <Link to="/demo" className="btn-ghost px-7 py-4 rounded-full text-[15px] font-medium inline-flex items-center gap-2">
              Demander un échange
            </Link>
          </div>
          <p className="mt-4 text-[12.5px] text-[#475569]">✓ Aperçu gratuit · ✓ CDC PDF · ✓ Lien partageable · ✓ Sans CB</p>

          {/* Key metrics */}
          <div className="mt-12 grid grid-cols-3 gap-3 max-w-2xl mx-auto">
            {cfg.keyMetrics.map((m) => (
              <div key={m.l} className="rounded-2xl border border-[var(--mc-mint)]/25 bg-emerald-50 p-5">
                <div className="text-[28px] sm:text-[32px] font-bold text-emerald-600 tracking-tight">{m.v}</div>
                <div className="text-[11px] uppercase tracking-[0.12em] text-[#475569] font-semibold mt-1">{m.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* PAINS */}
      <section className="py-20 lg:py-24">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#0369A1] font-semibold mb-3">Vos enjeux actuels</div>
            <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl font-semibold leading-tight">Ce qui vous freine aujourd&apos;hui.</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {cfg.pains.map((p) => (
              <div key={p.t} className="rounded-2xl border border-[#E2E8F0] bg-white shadow-sm p-6">
                <div className="w-11 h-11 rounded-lg grid place-items-center bg-[rgba(255,99,99,0.10)] text-red-400 mb-4">
                  <p.i className="w-5 h-5" />
                </div>
                <div className="text-[15px] font-semibold text-[#0F2B5B]">{p.t}</div>
                <p className="mt-2 text-[13.5px] text-[#475569] leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* BRICKS / SOLUTION */}
      <section className="py-20 lg:py-24 hex-soft">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-600 font-semibold mb-3">La solution MetaConnect</div>
            <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl font-semibold leading-tight">4 briques pour transformer votre quotidien.</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {cfg.bricks.map((b) => (
              <div key={b.t} className="rounded-2xl border border-slate-200 bg-[#0F2B5B] p-6">
                <div className="w-12 h-12 rounded-xl grid place-items-center bg-[rgba(110,231,197,0.10)]">
                  <b.i className="w-5.5 h-5.5 text-emerald-600" />
                </div>
                <div className="mt-4 text-[17px] font-semibold text-[#0F2B5B]">{b.t}</div>
                <p className="mt-2 text-[13.5px] text-[#475569] leading-relaxed" dangerouslySetInnerHTML={{ __html: b.d }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* QUOTE */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 text-center">
          <div className="text-5xl text-emerald-600/40 leading-none">&ldquo;</div>
          <p className="mt-2 text-[20px] lg:text-[22px] text-[#0F2B5B] leading-snug italic">{cfg.quote.text}</p>
          <div className="mt-5 text-[12.5px] uppercase tracking-[0.16em] text-[#0369A1] font-semibold">— {cfg.quote.who}</div>
        </div>
      </section>

      <div className="section-divider" />

      {/* FAQ */}
      <section className="py-20 lg:py-24">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#0369A1] font-semibold mb-3">Questions fréquentes</div>
            <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl font-semibold leading-tight">Vos questions, nos réponses.</h2>
          </div>
          <div className="space-y-3">
            {cfg.faq.map((f, i) => (
              <details key={i} className="group rounded-2xl border border-slate-200 bg-white p-5 open:border-[var(--mc-mint)]/40 transition-colors">
                <summary className="cursor-pointer list-none flex items-start gap-3">
                  <div className="shrink-0 w-6 h-6 rounded-full grid place-items-center bg-[rgba(110,231,197,0.10)] text-emerald-600 text-[12px] font-bold group-open:rotate-45 transition-transform">+</div>
                  <span className="text-[15px] font-semibold text-[#0F2B5B] leading-snug">{f.q}</span>
                </summary>
                <p className="mt-3 pl-9 text-[14px] text-[#475569] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 lg:py-28">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-600 font-semibold mb-3">5 minutes chrono</div>
          <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
            Prêt à transformer votre <span className="text-emerald-600">{cfg.eyebrow.split(" · ")[0]}</span> ?
          </h2>
          <p className="mt-5 text-[15px] text-[#475569] max-w-2xl mx-auto">
            Configurez votre aperçu interactif. Recevez votre cahier des charges PDF. Partagez le lien à vos associés ou votre bureau pour décision rapide.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Link to="/studio" className="btn-primary px-7 py-4 rounded-full text-[15.5px] font-semibold inline-flex items-center gap-2 shadow-[0_12px_40px_rgba(110,231,197,0.30)]" data-testid="persona-cta-final">
              <Wand2 size={17} /> Lancer ma configuration <ArrowRight size={16} />
            </Link>
          </div>
          <div className="mt-12 grid grid-cols-3 max-w-md mx-auto gap-3 text-[11.5px] text-[#475569]">
            <div className="flex items-center justify-center gap-1.5"><ShieldCheck size={13} /> HDS · RGPD</div>
            <div className="flex items-center justify-center gap-1.5"><Lock size={13} /> PSC · FC+</div>
            <div className="flex items-center justify-center gap-1.5"><Network size={13} /> DMP · MSSanté</div>
          </div>
        </div>
      </section>
    </div>
  );
}
